/**
 * @fileoverview Chat message component for displaying individual messages
 * @module ctl-chat-message
 * @version 1.0.0
 * 
 * @description
 * A chat message bubble that displays sender, timestamp, and message content.
 * Supports different alignments and styles based on sender type.
 * 
 * @example
 * <!-- User message -->
 * <ctl-chat-message sender="user" timestamp="2:30 PM">
 *   Hello, how can I help you?
 * </ctl-chat-message>
 * 
 * @example
 * <!-- Assistant message -->
 * <ctl-chat-message sender="assistant" timestamp="2:31 PM">
 *   I'm here to assist you with your questions.
 * </ctl-chat-message>
 */

const template = document.createElement("template");

template.innerHTML = `
<style>
  :host {
    --ctl-chat-msg-user-bg: var(--ctl-color-brand);
    --ctl-chat-msg-user-text: var(--ctl-color-on-brand);
    --ctl-chat-msg-assistant-bg: var(--ctl-color-surface-secondary, var(--ctl-color-overlay-2));
    --ctl-chat-msg-assistant-text: var(--ctl-color-text);
    --ctl-chat-msg-timestamp-color: var(--ctl-color-muted);
    --ctl-chat-msg-radius: var(--ctl-space-4);
    --ctl-chat-msg-padding: var(--ctl-space-3) var(--ctl-space-4);
    --ctl-chat-msg-margin: var(--ctl-space-2) 0;
    --ctl-chat-msg-max-width: 75%;

    display: flex;
    flex-direction: column;
    font-family: var(--ctl-font-sans, system-ui, -apple-system, Segoe UI, Roboto, Arial);
  }

  .message {
    max-width: var(--ctl-chat-msg-max-width);
    margin: var(--ctl-chat-msg-margin);
    padding: var(--ctl-chat-msg-padding);
    border-radius: var(--ctl-chat-msg-radius);
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  :host([sender="user"]) {
    align-items: flex-end;
  }

  :host([sender="user"]) .message {
    background: var(--ctl-chat-msg-user-bg);
    color: var(--ctl-chat-msg-user-text);
    align-self: flex-end;
  }

  :host([sender="assistant"]) {
    align-items: flex-start;
  }

  :host([sender="assistant"]) .message {
    background: var(--ctl-chat-msg-assistant-bg);
    color: var(--ctl-chat-msg-assistant-text);
    align-self: flex-start;
  }

  .timestamp {
    font-size: var(--ctl-font-size-xs);
    color: var(--ctl-chat-msg-timestamp-color);
    margin-top: var(--ctl-space-1);
    padding: 0 var(--ctl-space-2);
  }

  :host([sender="user"]) .timestamp {
    text-align: right;
  }

  :host([sender="assistant"]) .timestamp {
    text-align: left;
  }

  .timestamp:empty {
    display: none;
  }
</style>

<div class="message" part="message">
  <slot></slot>
</div>
<div class="timestamp" part="timestamp"></div>
`;

/**
 * Chat Message Web Component
 * 
 * @class CtlChatMessage
 * @extends HTMLElement
 * 
 * @attr {string} sender - Message sender type: "user" | "assistant" (default: "user")
 * @attr {string} timestamp - Timestamp text to display below message
 * 
 * @slot - Default slot for message content
 * 
 * @csspart message - The message bubble element
 * @csspart timestamp - The timestamp text element
 * 
 * @cssprop --ctl-chat-msg-user-bg - User message background color (default: var(--ctl-color-brand))
 * @cssprop --ctl-chat-msg-user-text - User message text color (default: var(--ctl-color-on-brand))
 * @cssprop --ctl-chat-msg-assistant-bg - Assistant message background color (default: var(--ctl-color-surface-secondary))
 * @cssprop --ctl-chat-msg-assistant-text - Assistant message text color (default: var(--ctl-color-text))
 * @cssprop --ctl-chat-msg-timestamp-color - Timestamp text color (default: var(--ctl-color-muted))
 * @cssprop --ctl-chat-msg-radius - Message border radius (default: 1rem)
 * @cssprop --ctl-chat-msg-max-width - Maximum message width (default: 75%)
 */
export class CtlChatMessage extends HTMLElement {
  static get observedAttributes() {
    return ["sender", "timestamp"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._timestampEl = this.shadowRoot.querySelector(".timestamp");
  }

  connectedCallback() {
    if (!this.hasAttribute("sender")) {
      this.setAttribute("sender", "user");
    }
    this._render();
  }

  attributeChangedCallback() {
    this._render();
  }

  _render() {
    const timestamp = this.getAttribute("timestamp") || "";
    const sender = this.getAttribute("sender") || "user";

    // Normalize sender to valid values
    if (sender !== "user" && sender !== "assistant") {
      this.setAttribute("sender", "user");
    }

    this._timestampEl.textContent = timestamp;
  }

  // Public API
  get sender() {
    return this.getAttribute("sender") || "user";
  }

  set sender(value) {
    if (value === "user" || value === "assistant") {
      this.setAttribute("sender", value);
    }
  }

  get timestamp() {
    return this.getAttribute("timestamp") || "";
  }

  set timestamp(value) {
    this.setAttribute("timestamp", value);
  }
}

if (!customElements.get("ctl-chat-message")) {
  customElements.define("ctl-chat-message", CtlChatMessage);
}

export default CtlChatMessage;
